import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Input,
  Message,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Typography,
} from '@arco-design/web-react'
import { IconDelete, IconPlus, IconSave, IconSearch } from '@arco-design/web-react/icon'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { Pill } from '@/utils/render'
import { formatUsdAsDisplay, getCurrencyConfig } from '@/utils/quota'
import type { OptionsAPI } from '../useOptions'
import PricingSimulator from './PricingSimulator'
import BillingExtrasPanel from './BillingExtrasPanel'
import { fetchOfficialPricing } from '@/api/officialPrice'
import {
  PAGE_SIZE,
  PRICE_SUFFIX,
  getModelStatusLabels,
  hasValue,
  useModelPricingEditorState,
  type FilterMode,
  type ModelPricingState,
} from './useModelPricingEditorState'

/** 用来把“$/百万”翻译成人能感知的成本：一次典型对话按 1k 输入 + 500 输出估 */
const TYPICAL_INPUT_TOKENS = 1000
const TYPICAL_OUTPUT_TOKENS = 500

const priceOf = (value: string): number | null => {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : null
}

const typicalChatUsd = (inputPrice?: string, outputPrice?: string): number | null => {
  const input = inputPrice === undefined ? null : priceOf(inputPrice)
  const output = outputPrice === undefined ? null : priceOf(outputPrice)
  if (input === null && output === null) return null
  return ((input ?? 0) * TYPICAL_INPUT_TOKENS + (output ?? 0) * TYPICAL_OUTPUT_TOKENS) / 1_000_000
}

/** 只保留有意义的位数：0.075 → 0.075，0.6 → 0.6，1.2345 → 1.2345 */
const formatPrice = (value: number): string => {
  if (!Number.isFinite(value)) return '-'
  const fixed = value.toFixed(4)
  return fixed.replace(/0+$/, '').replace(/\.$/, '')
}

interface PriceInputProps {
  label: string
  value: string
  placeholder: string
  onChange: (value: string) => void
  suffix?: string
  disabled?: boolean
  extraText?: string
  headerAction?: React.ReactNode
  hidden?: boolean
  switchKey?: string
}

function PriceInput({
  label,
  value,
  placeholder,
  onChange,
  suffix = PRICE_SUFFIX,
  disabled = false,
  extraText = '',
  headerAction = null,
  hidden = false,
  switchKey,
}: PriceInputProps) {
  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center justify-between gap-3 font-medium text-slate-700">
        <span>{label}</span>
        <span key={switchKey}>{headerAction}</span>
      </div>
      {!hidden ? (
        <Input
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          suffix={suffix}
          disabled={disabled}
        />
      ) : null}
      {extraText ? <div className="mt-1 text-xs text-slate-500">{extraText}</div> : null}
    </div>
  )
}

export interface VisualModelPricingEditorProps {
  options: OptionsAPI
  candidateModelNames?: string[]
  filterMode?: FilterMode
  allowAddModel?: boolean
  allowDeleteModel?: boolean
  showConflictFilter?: boolean
  listDescription?: string
  emptyTitle?: string
  emptyDescription?: string
}

const NO_CANDIDATE_MODELS: string[] = []

export default function VisualModelPricingEditor({
  options,
  candidateModelNames = NO_CANDIDATE_MODELS,
  filterMode = 'all',
  allowAddModel = true,
  allowDeleteModel = true,
  showConflictFilter = true,
  listDescription = '',
  emptyTitle = '',
  emptyDescription = '',
}: VisualModelPricingEditorProps) {
  const { isMobile } = useBreakpoint()
  const [addVisible, setAddVisible] = useState(false)
  const [batchVisible, setBatchVisible] = useState(false)
  const [newModelName, setNewModelName] = useState('')
  // 官方价填充：倍数与加载态
  const [officialMultiplier, setOfficialMultiplier] = useState(1)
  const [officialLoading, setOfficialLoading] = useState(false)

  const {
    selectedModel,
    selectedModelName,
    selectedModelNames,
    setSelectedModelName,
    setSelectedModelNames,
    searchText,
    setSearchText,
    currentPage,
    setCurrentPage,
    loading,
    parseErrors,
    hasParseErrors,
    canSave,
    conflictOnly,
    setConflictOnly,
    filteredModels,
    pagedData,
    selectedWarnings,
    previewRows,
    isOptionalFieldEnabled,
    handleOptionalFieldToggle,
    handleNumericFieldChange,
    handleBillingModeChange,
    handleSubmit,
    addModel,
    deleteModel,
    applySelectedModelPricing,
    effectiveForModel,
    snapshotByModel,
  } = useModelPricingEditorState({
    options,
    candidateModelNames,
    filterMode,
  })

  const fillFromOfficialPricing = async () => {
    if (!selectedModel?.name) return
    setOfficialLoading(true)
    try {
      const official = await fetchOfficialPricing(selectedModel.name)
      if (!official || (official.inputPerMillion === null && official.fixedPrice === null)) {
        Message.warning(`models.dev 上没有 ${selectedModel.name} 的参考价`)
        return
      }
      const factor = officialMultiplier > 0 ? officialMultiplier : 1
      if (official.fixedPrice !== null && official.inputPerMillion === null) {
        handleBillingModeChange('per-request')
        handleNumericFieldChange(
          'fixedPrice',
          String(Number((official.fixedPrice * factor).toFixed(6)))
        )
        Message.success(`已按参考价填入固定价 ×${factor}`)
        return
      }
      if (official.inputPerMillion !== null) {
        handleNumericFieldChange(
          'inputPrice',
          String(Number((official.inputPerMillion * factor).toFixed(6)))
        )
        if (official.outputPerMillion !== null) {
          handleNumericFieldChange(
            'completionPrice',
            String(Number((official.outputPerMillion * factor).toFixed(6)))
          )
        }
        if (official.cacheReadPerMillion !== null) {
          handleNumericFieldChange(
            'cachePrice',
            String(Number((official.cacheReadPerMillion * factor).toFixed(6)))
          )
        }
        Message.success(`已按参考价填入（×${factor}），缓存写价请按需保留或补填`)
      }
    } catch (err) {
      Message.error(
        `拉取参考价失败：${err instanceof Error ? err.message : '可能无法访问 models.dev'}`
      )
    } finally {
      setOfficialLoading(false)
    }
  }

  const columns = useMemo(
    () => [
      {
        title: '模型名称',
        dataIndex: 'name',
        fixed: 'left' as const,
        width: 200,
        render: (_: unknown, record: ModelPricingState) => (
          <Space>
            <Button
              type="text"
              onClick={() => setSelectedModelName(record.name)}
              style={{
                color: record.name === selectedModelName ? 'rgb(var(--primary-6))' : undefined,
                padding: 0,
              }}
            >
              {record.name}
            </Button>
            {selectedModelNames.includes(record.name) ? (
              <Pill variant="success">已勾选</Pill>
            ) : null}
          </Space>
        ),
      },
      {
        title: '计费方式',
        dataIndex: 'billingMode',
        width: 120,
        render: (_: unknown, record: ModelPricingState) => (
          <Pill variant={record.billingMode === 'per-request' ? 'info' : 'violet'}>
            {record.billingMode === 'per-request' ? '按次计费' : '按量计费'}
          </Pill>
        ),
      },
      {
        title: '实际生效价（含默认）',
        width: 260,
        render: (_: unknown, record: ModelPricingState) => {
          const effective = effectiveForModel(record.name, {
            ModelPrice: record.fixedPrice,
            ModelRatio: record.rawRatios.modelRatio,
            CompletionRatio: record.rawRatios.completionRatio,
            CacheRatio: record.rawRatios.cacheRatio,
            CreateCacheRatio: record.rawRatios.createCacheRatio,
          })
          if (!effective) return <span className="text-xs text-slate-400">-</span>
          const fmt = (v: number | null) => (v === null ? '-' : formatUsdAsDisplay(v))
          const usesDefaults = effective.defaultedKeys.some((k) =>
            ['ModelRatio', 'CompletionRatio', 'CacheRatio', 'CreateCacheRatio'].includes(k)
          )
          return (
            <div className="space-y-1 text-xs">
              {effective.fixedPrice ? (
                <div className="tabular-nums">按次 ${formatPrice(effective.fixedPrice)}</div>
              ) : (
                <>
                  <div className="tabular-nums">
                    入 {fmt(effective.inputPerMillion)} · 出 {fmt(effective.outputPerMillion)}
                  </div>
                  <div className="tabular-nums text-[var(--console-text-faint)]">
                    缓存读 {fmt(effective.cacheReadPerMillion)} · 写{' '}
                    {fmt(effective.cacheWritePerMillion)}
                  </div>
                  <div className="tabular-nums text-[var(--console-text-muted)]">
                    对话 ≈{' '}
                    {formatUsdAsDisplay(
                      typicalChatUsd(
                        effective.inputPerMillion === null
                          ? undefined
                          : String(effective.inputPerMillion),
                        effective.outputPerMillion === null
                          ? undefined
                          : String(effective.outputPerMillion)
                      ) ?? 0,
                      5
                    )}
                  </div>
                </>
              )}
              {usesDefaults ? <Pill variant="warning">含引擎默认</Pill> : null}
            </div>
          )
        },
      },
      {
        title: '状态',
        width: 200,
        render: (_: unknown, record: ModelPricingState) => (
          <Space wrap size="mini">
            {getModelStatusLabels(record).map((label) => (
              <Pill key={label.text} variant={label.variant}>
                {label.text}
              </Pill>
            ))}
          </Space>
        ),
      },
      {
        title: '操作',
        width: 80,
        fixed: 'right' as const,
        render: (_: unknown, record: ModelPricingState) =>
          allowDeleteModel ? (
            <Button
              type="text"
              status="danger"
              icon={<IconDelete />}
              onClick={() => deleteModel(record.name)}
            />
          ) : null,
      },
    ],
    [allowDeleteModel, deleteModel, selectedModelName, selectedModelNames, setSelectedModelName]
  )

  const handleAddModel = () => {
    if (addModel(newModelName)) {
      setNewModelName('')
      setAddVisible(false)
    }
  }

  return (
    <>
      <Space direction="vertical" size="medium" style={{ width: '100%' }}>
        {hasParseErrors ? (
          <Alert
            type="error"
            content={
              <div>
                <div className="font-medium mb-1">以下 option JSON 解析失败，无法保存价格设置</div>
                {Object.entries(parseErrors).map(([key, error]) => (
                  <div key={key} className="text-sm">
                    {key}：{error}
                  </div>
                ))}
                <div className="text-xs mt-2 text-slate-500">
                  请前往「模型倍率设置」高级 Tab 修复原始 JSON 后再保存。
                </div>
              </div>
            }
          />
        ) : null}
        <Space wrap className="settings-toolbar-compact">
          {allowAddModel ? (
            <Button icon={<IconPlus />} onClick={() => setAddVisible(true)} long={isMobile}>
              添加模型
            </Button>
          ) : null}
          <Button
            type="primary"
            icon={<IconSave />}
            loading={loading}
            disabled={!canSave}
            onClick={handleSubmit}
            long={isMobile}
          >
            保存价格设置
          </Button>
          <Button
            disabled={!selectedModel || selectedModelNames.length === 0}
            onClick={() => setBatchVisible(true)}
            long={isMobile}
          >
            批量应用当前模型价格
            {selectedModelNames.length > 0 ? ` (${selectedModelNames.length})` : ''}
          </Button>
          <Input
            prefix={<IconSearch />}
            placeholder="搜索模型名称"
            value={searchText}
            onChange={setSearchText}
            style={{ width: isMobile ? '100%' : 220 }}
            allowClear
          />
          {showConflictFilter ? (
            <Checkbox checked={conflictOnly} onChange={setConflictOnly}>
              只看冲突
            </Checkbox>
          ) : null}
        </Space>

        <div className="settings-info-strip text-xs leading-relaxed">
          <strong>计价维度：</strong>输入价是唯一基准（ModelRatio，$1/百万 token = ratio 2）； 输出
          = 输入 × 补全倍率；缓存<span className="font-medium">命中/读</span> = 输入 × 缓存倍率；
          缓存<span className="font-medium">写入/创建</span> = 输入 × 缓存创建倍率（Claude 1 小时 /
          OpenAI 12 小时会再乘固定倍数）；图片、音频各自一档；最后统一乘
          <span className="font-medium">分组倍率</span>。
          工具调用（联网搜索等）与表达式计费另算，见下方两张表。
        </div>
        {listDescription ? <div className="text-sm text-slate-500">{listDescription}</div> : null}
        {selectedModelNames.length > 0 ? (
          <div className="settings-info-strip">已勾选 {selectedModelNames.length} 个模型</div>
        ) : null}

        <div
          className="ratio-pricing-grid grid gap-3"
          style={{
            gridTemplateColumns: isMobile
              ? 'minmax(0, 1fr)'
              : 'minmax(360px, 1.05fr) minmax(420px, 1fr)',
          }}
        >
          <div
            className="settings-section ratio-model-list-panel"
            style={{ order: isMobile ? 2 : 0 }}
          >
            <Table
              columns={columns}
              data={pagedData}
              rowKey="name"
              rowSelection={{
                selectedRowKeys: selectedModelNames,
                onChange: (keys) => setSelectedModelNames(keys as string[]),
              }}
              pagination={{
                current: currentPage,
                pageSize: PAGE_SIZE,
                total: filteredModels.length,
                onChange: setCurrentPage,
                showTotal: true,
              }}
              onRow={(record) => ({
                onClick: () => setSelectedModelName(record.name),
                style: {
                  background: selectedModelNames.includes(record.name)
                    ? 'var(--color-success-light-1)'
                    : record.name === selectedModelName
                      ? 'var(--color-primary-light-1)'
                      : undefined,
                  cursor: 'pointer',
                },
              })}
              scroll={{ x: 900, y: isMobile ? undefined : 'calc(100vh - 260px)' }}
              noDataElement={
                <Empty description={emptyTitle || '暂无模型'} style={{ padding: '24px 0' }} />
              }
            />
          </div>

          <div
            className="settings-section ratio-model-editor-panel"
            style={{ order: isMobile ? 1 : 0 }}
          >
            <div className="ratio-editor-heading">
              <div className="ratio-editor-title">
                {selectedModel ? selectedModel.name : '模型计费编辑器'}
              </div>
              {selectedModel ? (
                <Pill variant={selectedModel.billingMode === 'per-request' ? 'info' : 'violet'}>
                  {selectedModel.billingMode === 'per-request' ? '按次计费' : '按量计费'}
                </Pill>
              ) : null}
            </div>
            {!selectedModel ? (
              <Empty
                description={
                  emptyDescription || emptyTitle || '请先新增模型或从左侧列表选择一个模型'
                }
                style={{ padding: '32px 0' }}
              />
            ) : (
              <div>
                <div className="mb-4">
                  <div className="mb-2 font-medium text-slate-700">计费方式</div>
                  <Radio.Group
                    type="button"
                    value={selectedModel.billingMode}
                    onChange={handleBillingModeChange}
                  >
                    <Radio value="per-token">按量计费</Radio>
                    <Radio value="per-request">按次计费</Radio>
                  </Radio.Group>
                  <div className="mt-2 text-xs text-slate-500">
                    这个界面默认按价格填写，保存时会自动换算回后端需要的倍率 JSON。
                  </div>
                </div>

                {selectedWarnings.length > 0 ? (
                  <Alert
                    type="warning"
                    className="!mb-4"
                    content={
                      <div>
                        <div className="font-medium mb-1">当前提示</div>
                        {selectedWarnings.map((warning) => (
                          <div key={warning} className="text-sm">
                            {warning}
                          </div>
                        ))}
                      </div>
                    }
                  />
                ) : null}

                {selectedModel.billingMode === 'per-request' ? (
                  <PriceInput
                    label="固定价格"
                    value={selectedModel.fixedPrice}
                    placeholder="输入每次调用价格"
                    suffix="$/次"
                    onChange={(value) => handleNumericFieldChange('fixedPrice', value)}
                    extraText="适合 MJ / 任务类等按次收费模型。"
                  />
                ) : (
                  <>
                    <div className="settings-subsection mb-3">
                      <div className="settings-section-title mb-3">基础价格</div>
                      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-[var(--console-border)] p-2">
                        <span className="text-xs text-[var(--console-text-muted)]">
                          不知道填多少？按参考价填充：
                        </span>
                        <Select
                          size="mini"
                          value={officialMultiplier}
                          onChange={(v: string | number) => setOfficialMultiplier(Number(v))}
                          options={[
                            { label: '参考价 ×1.0', value: 1 },
                            { label: '参考价 ×0.9', value: 0.9 },
                            { label: '参考价 ×0.8', value: 0.8 },
                            { label: '参考价 ×1.2', value: 1.2 },
                          ]}
                          style={{ width: 140 }}
                        />
                        <Button
                          size="mini"
                          loading={officialLoading}
                          onClick={fillFromOfficialPricing}
                        >
                          填充到本模型
                        </Button>
                        <span className="text-[11px] text-[var(--console-text-faint)]">
                          数据源
                          models.dev；同模型多供应商时取**最低输入价**，可能是第三方渠道价，请核对。会覆盖输入/输出/缓存读价。
                        </span>
                      </div>
                      <PriceInput
                        label={`输入价格（${getCurrencyConfig().prefix || '$'} / 百万 token）`}
                        value={selectedModel.inputPrice}
                        placeholder="例如 0.15（官方 gpt-4o-mini 约 0.15 USD）"
                        onChange={(value) => handleNumericFieldChange('inputPrice', value)}
                        extraText={(() => {
                          const inputUsd = priceOf(selectedModel.inputPrice)
                          if (inputUsd === null)
                            return '填的是「每百万 token」的单价；填 0.15 表示输入 100 万 token 收 0.15 美元。'
                          const chat = typicalChatUsd(
                            selectedModel.inputPrice,
                            selectedModel.completionPrice
                          )
                          const parts = [`≈ ${formatUsdAsDisplay(inputUsd)} / 百万 token`]
                          if (chat !== null) {
                            parts.push(
                              `一次典型对话（1k 输入 + 500 输出）约 ${formatUsdAsDisplay(chat, 5)}`
                            )
                          }
                          return parts.join(' · ')
                        })()}
                      />
                      {selectedModel.completionRatioLocked ? (
                        <Alert
                          type="warning"
                          className="!mb-3"
                          content={`该模型补全倍率由后端固定为 ${selectedModel.lockedCompletionRatio || '-'}。补全价格不能在这里修改。`}
                        />
                      ) : null}
                      <PriceInput
                        label={`补全价格（${getCurrencyConfig().prefix || '$'} / 百万 token）`}
                        value={selectedModel.completionPrice}
                        placeholder="例如 0.6（通常是输入的 3~4 倍）"
                        onChange={(value) => handleNumericFieldChange('completionPrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-completionPrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'completionPrice')}
                            disabled={selectedModel.completionRatioLocked}
                            onChange={(checked) =>
                              handleOptionalFieldToggle('completionPrice', checked)
                            }
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'completionPrice')}
                        disabled={
                          !hasValue(selectedModel.inputPrice) || selectedModel.completionRatioLocked
                        }
                        extraText={
                          selectedModel.completionRatioLocked
                            ? `后端固定倍率：${selectedModel.lockedCompletionRatio || '-'}。该字段仅展示换算后的价格。`
                            : !isOptionalFieldEnabled(selectedModel, 'completionPrice')
                              ? '当前未启用，需要时再打开即可。'
                              : ''
                        }
                      />
                      <PriceInput
                        label="缓存读取价格"
                        value={selectedModel.cachePrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('cachePrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-cachePrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'cachePrice')}
                            onChange={(checked) => handleOptionalFieldToggle('cachePrice', checked)}
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'cachePrice')}
                        disabled={!hasValue(selectedModel.inputPrice)}
                        extraText={
                          !isOptionalFieldEnabled(selectedModel, 'cachePrice')
                            ? '当前未启用，需要时再打开即可。'
                            : ''
                        }
                      />
                      <PriceInput
                        label="缓存创建价格"
                        value={selectedModel.createCachePrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('createCachePrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-createCachePrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'createCachePrice')}
                            onChange={(checked) =>
                              handleOptionalFieldToggle('createCachePrice', checked)
                            }
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'createCachePrice')}
                        disabled={!hasValue(selectedModel.inputPrice)}
                        extraText={
                          !isOptionalFieldEnabled(selectedModel, 'createCachePrice')
                            ? '当前未启用，需要时再打开即可。'
                            : `这是 5 分钟缓存写的基准价。Claude 1 小时缓存写 = 该价 ×1.6，OpenAI 12 小时缓存写 = ×2，后端自动派生，无需单独配置。`
                        }
                      />
                    </div>

                    <div className="settings-subsection mb-3">
                      <div className="mb-2">
                        <div className="settings-section-title">扩展价格</div>
                        <div className="settings-section-desc">
                          这些价格都是可选项，不填也可以。
                        </div>
                      </div>
                      <PriceInput
                        label="图片输入价格"
                        value={selectedModel.imagePrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('imagePrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-imagePrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'imagePrice')}
                            onChange={(checked) => handleOptionalFieldToggle('imagePrice', checked)}
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'imagePrice')}
                        disabled={!hasValue(selectedModel.inputPrice)}
                        extraText="按图片输入的 token 计价。图像生成按张收费请用「固定价」，不要用这里。"
                      />
                      <PriceInput
                        label="音频输入价格"
                        value={selectedModel.audioInputPrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('audioInputPrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-audioInputPrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'audioInputPrice')}
                            onChange={(checked) =>
                              handleOptionalFieldToggle('audioInputPrice', checked)
                            }
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'audioInputPrice')}
                        disabled={!hasValue(selectedModel.inputPrice)}
                      />
                      <PriceInput
                        label="音频补全价格"
                        value={selectedModel.audioOutputPrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('audioOutputPrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-audioOutputPrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'audioOutputPrice')}
                            disabled={!isOptionalFieldEnabled(selectedModel, 'audioInputPrice')}
                            onChange={(checked) =>
                              handleOptionalFieldToggle('audioOutputPrice', checked)
                            }
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'audioOutputPrice')}
                        disabled={!hasValue(selectedModel.audioInputPrice)}
                        extraText={
                          !isOptionalFieldEnabled(selectedModel, 'audioInputPrice')
                            ? '请先开启并填写音频输入价格。'
                            : !isOptionalFieldEnabled(selectedModel, 'audioOutputPrice')
                              ? '当前未启用，需要时再打开即可。'
                              : ''
                        }
                      />
                    </div>
                  </>
                )}

                <div className="settings-subsection">
                  <div className="settings-section-title mb-2">这个价格意味着什么</div>
                  <div className="settings-section-desc mb-2">
                    按一次典型对话 = 1,000 输入 token + 500 输出 token 估算。
                  </div>
                  {(() => {
                    const inputUsd = priceOf(selectedModel.inputPrice)
                    const outputUsd = priceOf(selectedModel.completionPrice)
                    const chatUsd = typicalChatUsd(
                      selectedModel.inputPrice,
                      selectedModel.completionPrice
                    )
                    if (inputUsd === null && outputUsd === null) {
                      return (
                        <div className="text-xs text-slate-400">
                          先填一个输入价，这里会算出客户实际付多少钱。
                        </div>
                      )
                    }
                    // 每 10 个展示货币单位能跑多少次对话
                    const perTen = chatUsd && chatUsd > 0 ? Math.round(10 / chatUsd) : null
                    // 进价与毛利（如果配了渠道进价表）
                    let costChatUsd: number | null = null
                    try {
                      const raw = options.get('channel_cost_setting.UpstreamModelCost', '{}')
                      const table = raw
                        ? (JSON.parse(raw) as Record<string, Record<string, number>>)
                        : {}
                      const cost = table[selectedModel.name]
                      if (cost) {
                        const costInput = Number(cost.InputPerMTokUsd ?? 0)
                        const costOutput = Number(cost.OutputPerMTokUsd ?? 0)
                        if (costInput || costOutput) {
                          costChatUsd = typicalChatUsd(String(costInput), String(costOutput))
                        }
                      }
                    } catch {
                      costChatUsd = null
                    }
                    const margin =
                      costChatUsd !== null && chatUsd && chatUsd > 0
                        ? ((chatUsd - costChatUsd) / chatUsd) * 100
                        : null
                    return (
                      <div className="space-y-1 text-xs">
                        <div>
                          一次典型对话约{' '}
                          <strong className="tabular-nums">
                            {chatUsd === null ? '-' : formatUsdAsDisplay(chatUsd, 5)}
                          </strong>
                        </div>
                        {perTen !== null ? (
                          <div className="text-[var(--console-text-muted)]">
                            每 10 个货币单位大约能跑{' '}
                            <strong className="tabular-nums">{perTen.toLocaleString()}</strong>{' '}
                            次这样的对话
                          </div>
                        ) : null}
                        {costChatUsd !== null ? (
                          <div className="text-[var(--console-text-muted)]">
                            进价约 {formatUsdAsDisplay(costChatUsd, 5)}
                            {margin !== null ? (
                              <>
                                {' '}
                                · 毛利{' '}
                                <strong
                                  className={margin >= 0 ? 'text-emerald-600' : 'text-rose-600'}
                                >
                                  {margin.toFixed(0)}%
                                </strong>
                              </>
                            ) : null}
                          </div>
                        ) : (
                          <div className="text-[var(--console-text-muted)]">
                            未配置该模型进价，毛利请到「进价 / 成本」里补
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>

                <div className="settings-subsection">
                  <div className="settings-section-title mb-2">保存预览</div>
                  <div className="settings-section-desc mb-3">
                    下面展示该模型保存后会写入的后端 key 与 value。
                  </div>
                  <div className="divide-y divide-slate-100 text-sm">
                    {previewRows.map((row) => (
                      <div
                        key={row.key}
                        className="grid gap-2 py-2"
                        style={{ gridTemplateColumns: 'minmax(140px, 180px) 1fr' }}
                      >
                        <Typography.Text bold className="!text-xs">
                          {row.label}
                        </Typography.Text>
                        <Typography.Text className="!text-xs font-mono">
                          {row.value}
                        </Typography.Text>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Space>

      {allowAddModel ? (
        <Modal
          title="添加模型"
          visible={addVisible}
          onCancel={() => {
            setAddVisible(false)
            setNewModelName('')
          }}
          onOk={handleAddModel}
        >
          <Input
            value={newModelName}
            placeholder="输入模型名称，例如 gpt-4.1"
            onChange={setNewModelName}
          />
        </Modal>
      ) : null}

      <Modal
        title="批量应用当前模型价格"
        visible={batchVisible}
        onCancel={() => setBatchVisible(false)}
        onOk={() => {
          if (applySelectedModelPricing()) setBatchVisible(false)
        }}
      >
        <div className="space-y-2 text-sm text-slate-600">
          {selectedModel ? (
            <>
              <div>
                将把当前编辑中的模型 <strong>{selectedModel.name}</strong>{' '}
                的价格配置，批量应用到已勾选的 {selectedModelNames.length} 个模型。
              </div>
              <div className="text-amber-700">
                此操作会覆盖已勾选模型现有的定价配置，包括固定价与全部按量倍率键。请确认后再继续。
              </div>
            </>
          ) : (
            '请先选择一个作为模板的模型'
          )}
        </div>
      </Modal>

      <div className="mt-4 space-y-3">
        <BillingExtrasPanel options={options} />
        <PricingSimulator entries={snapshotByModel} defaultModel={selectedModel?.name} />
      </div>
    </>
  )
}
